// Shared constants + pure pricing/weight-math helpers for the Purchases module.
import { formatINR, formatNum } from "@/lib/api";

export const PURCHASE_TYPES = [
  { value: "gold", label: "Gold" }, { value: "diamond", label: "Diamond" },
  { value: "jewellery", label: "Jewellery" }, { value: "melting", label: "Melting / Scrap Gold" },
  { value: "raw_material", label: "Other Raw Material" }, { value: "packaging", label: "Packaging / Misc" },
];
export const KARATS = ["24K", "22K", "18K", "14K", "9K"];

// Ledger amounts are dual-currency (Gold grams OR Fiat INR) — a small named helper keeps
// every "gold ? Xg : ₹X" spot consistent and avoids repeating a nested ternary inline.
export function formatLedgerAmount(kind, value) {
  return kind === "gold" ? `${formatNum(value)}g` : formatINR(value);
}
// Label suffix for the Making Charges input, based on the selected Making Type.
export function makingLabelSuffix(makingType) {
  if (makingType === "percent") return "(%)";
  if (makingType === "per_gram") return "(₹/g)";
  return "(₹)";
}
export const PAY_STATUS_STYLE = {
  paid: "bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]",
  partial: "bg-[#FBF1DF] text-[#7A4B12] border border-[#E7D0A8]",
  unpaid: "bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]",
};
export const STATUS_STYLE = {
  confirmed: "bg-[#EAF1F7] text-[#1E3E5C] border border-[#C7D7E6]",
  cancelled: "bg-[#F0E9E9] text-[#5A4A4A] border border-[#DDD0D0]",
};

export const PURITY_MAP = { "24K": 1, "22K": 0.916, "18K": 0.75, "14K": 0.585, "9K": 0.375 };
export const CARAT_TO_GRAM = 0.2; // 1 carat = 0.2 grams — matches retail product pricing (Diamond Pricing 2.0)

// Melted/scrap gold is graded per-mille (e.g. 995, 916) rather than a karat label — same convention
// used in Job Work's "Receive from Karigar" form.
export const FINE_PURITY_PRESETS = [
  { value: "999", label: "999 (99.9% fine)" },
  { value: "995", label: "995 (99.5%)" },
  { value: "916", label: "916 (91.6% · 22K)" },
  { value: "750", label: "750 (75% · 18K)" },
  { value: "585", label: "585 (58.5% · 14K)" },
  { value: "375", label: "375 (37.5% · 9K)" },
  { value: "custom", label: "Custom" },
];

export const emptyItem = () => ({ _key: (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`), purchase_type: "gold", item_name: "", linked_product_id: "", quantity: 1, gross_weight_g: 0, net_weight_g: 0, stone_weight_g: 0, purity: "22K", purity_preset: "916", purity_fine_pct: "916", gold_rate_per_gram: 0, diamond_carat: 0, diamond_pieces: 0, diamond_quality: "", diamond_rate_per_carat: 0, certificate_no: "", making_type: "flat", making_charges: 0, certification_charges: 0, hallmarking_charges: 0, wastage_pct: 0, other_charges: 0, line_amount: "", notes: "" });
export const emptyForm = () => ({ supplier_id: "", purchase_date: new Date().toISOString().slice(0, 10), items: [emptyItem()], gst_pct: 3, payment_method: "bank_transfer", notes: "", attachments: [] });

export function round3(v) { return Math.round((Number(v) || 0) * 1000) / 1000; }

// Gross Weight = Net Weight + Stone Weight — enforced for every gold-bearing line (gold, jewellery,
// melting). For Jewellery specifically, the strict rule is: Stone Wt is ALWAYS derived from
// Diamond Wt (ct x 0.2) — never freely typed — and Net Wt is ALWAYS Gross − Stone, recomputed
// live the instant Gross Wt or Diamond Wt changes (both fields are read-only in the UI). Gold and
// Melting lines (no diamonds) keep the "enter any two of three, the third is calculated" flow.
// Mirrors the backend's `_resolve_weights()` exactly.
export function resolveWeights(it) {
  if (it.purchase_type === "jewellery") {
    const gross = Number(it.gross_weight_g) || 0;
    const stone = round3((Number(it.diamond_carat) || 0) * CARAT_TO_GRAM);
    const net = round3(Math.max(0, gross - stone));
    return { gross_weight_g: round3(gross), net_weight_g: net, stone_weight_g: stone };
  }
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  let gross = num(it.gross_weight_g);
  let net = num(it.net_weight_g);
  let stone = num(it.stone_weight_g);
  const provided = [gross, net, stone].filter((v) => v !== null).length;
  if (provided >= 2) {
    if (gross === null) gross = (net || 0) + (stone || 0);
    else if (net === null) net = Math.max(0, gross - (stone || 0));
    else if (stone === null) stone = Math.max(0, gross - (net || 0));
    else net = Math.max(0, gross - stone); // all three entered — Gross & Stone are ground truth
  } else {
    gross = gross || 0; net = net || 0; stone = stone || 0;
  }
  return { gross_weight_g: round3(gross), net_weight_g: round3(net), stone_weight_g: round3(stone) };
}
export function fineGoldGrams(it) {
  const w = resolveWeights(it);
  const factor = it.purchase_type === "melting" ? (Number(it.purity_fine_pct) || 0) / 1000 : (PURITY_MAP[it.purity] ?? 1);
  return w.net_weight_g * factor;
}
export function itemGoldValue(it) {
  if (!["gold", "jewellery", "melting"].includes(it.purchase_type)) return 0;
  return fineGoldGrams(it) * (Number(it.gold_rate_per_gram) || 0);
}
export function itemDiamondValue(it) {
  if (it.purchase_type !== "diamond" && it.purchase_type !== "jewellery") return 0;
  return (Number(it.diamond_carat) || 0) * (Number(it.diamond_rate_per_carat) || 0);
}
// Making charge — same convention as the retail Product pricing module: flat (₹), percent (% of
// metal value) or per_gram (₹/g x Net Weight) — keeps Purchase and Product pricing identical.
export function itemMakingValue(it) {
  const input = Number(it.making_charges) || 0;
  const type = it.making_type || "flat";
  if (type === "percent") return itemGoldValue(it) * input / 100;
  if (type === "per_gram") return input * resolveWeights(it).net_weight_g;
  return input;
}

export function itemPreviewTotal(it) {
  let lineAmount = it.line_amount === "" || it.line_amount === null || it.line_amount === undefined ? null : Number(it.line_amount);
  const goldValue = itemGoldValue(it);
  if (lineAmount === null) lineAmount = goldValue + itemDiamondValue(it);
  // Wastage applies to the METAL value only (never diamonds) — matches trade convention.
  const wastageAmount = goldValue * (Number(it.wastage_pct) || 0) / 100;
  const certification = Number(it.certification_charges) || 0;
  const hallmarking = Number(it.hallmarking_charges) || 0;
  return Math.round(lineAmount + itemMakingValue(it) + wastageAmount + certification + hallmarking + (Number(it.other_charges) || 0));
}
